import { useEffect, useMemo } from "react";
import {
  filterTypes,
  type FilterType,
} from "../../../../../Findings/FindingsUtils";
import { processFilter, processScope } from "../../../../AppViewUtils";
import "./FilterDisplayByFilterToExecute.scss";
import { SkeletonLoader } from "@SeemplicityDev/shared";
import FilterChipButton from "../../../../FiltersBar/FilterChip/FilterChipButton";
import { FieldData, FilterItem } from "../../../CommonFilterTypes";
import {
  FILTER_TO_EXECUTE_PARSE_ERROR,
  parseFilterQuery,
} from "../../../parseFilterQuery";
import useLogger, { LOG_SEVERITY } from "../../../../../Logger/useLogger";
import useRemediationItemsSplit from "../../../../../General/useRemediationItemsSplit";
import { isBareFilterKey } from "../../../filterKey";

export interface FilterDisplayByFilterToExecuteProps {
  filterToExecute?: string;
  filterType: FilterType;
  statusesData?: any[] | null;
  fieldsData?: FieldData[];
  fieldsLoading?: boolean;
  displayFullFilterValue?: boolean;
  showFirstRowsCount?: number;
  showFirstConditionsInRowCount?: number;
  filterContext?: string;
  className?: string;
  emptyState?: React.ReactNode;
  showLegacyFilterWarning?: boolean;
}

export const LEGACY_FILTER_WARNING_TEXT =
  "This Filter will soon be unsupported";

interface FilterRow {
  conditions: FilterItem[];
}

const hasLegacyFilterKey = (rows: FilterRow[] | undefined): boolean =>
  !!rows?.some((row) =>
    row?.conditions?.some((condition) => isBareFilterKey(condition?.field))
  );

const FilterDisplayByFilterToExecute: React.FC<
  FilterDisplayByFilterToExecuteProps
> = ({
  filterToExecute,
  filterType,
  statusesData,
  fieldsData,
  fieldsLoading,
  displayFullFilterValue = false,
  showFirstRowsCount = 0,
  showFirstConditionsInRowCount = 0,
  className = "",
  emptyState = null,
  showLegacyFilterWarning = false,
}) => {
  const { logError } = useLogger();
  const isRemediationItemsSplit = useRemediationItemsSplit();

  // Callers pass a filter string from several sources — the applied filter,
  // a URL param, or another subject's `filter_data` (popovers, queues,
  // aggregation rules). Any of them can be malformed, so render defensively
  // and report; recovering the *applied* filter is the caller's job.
  const { filter, parseError } = useMemo(
    () => parseFilterQuery(filterToExecute),
    [filterToExecute]
  );

  useEffect(() => {
    if (!parseError) {
      return;
    }
    logError(parseError, FILTER_TO_EXECUTE_PARSE_ERROR, {
      severity: LOG_SEVERITY.CRITICAL,
      extra: { filterType, source: "display" },
    });
  }, [parseError, filterToExecute, filterType, logError]);

  let filterObj: FilterRow[] | undefined;
  if (filterType === filterTypes.FILTER && filter) {
    filterObj = processFilter(filter)?.filterConditions;
  }
  if (filterType === filterTypes.SCOPE && filter) {
    filterObj = processScope(filter)?.scopeConditions;
  }

  const isLegacyFilterWarningShown =
    showLegacyFilterWarning &&
    isRemediationItemsSplit &&
    filterType === filterTypes.FILTER &&
    hasLegacyFilterKey(filterObj);

  const getFilterRow = (filter: FilterItem, index: number) => (
    <div className={"filter-item-wrap"} key={index}>
      <div className={"filter-row-item"}>
        <FilterChipButton
          filterItem={filter}
          isActive={false}
          isBlink={false}
          displayFullValue={displayFullFilterValue}
          allowRemove={false}
          fieldsData={fieldsData ?? []}
          statusesData={statusesData ?? []}
          filtersToPin={[]}
        />
      </div>
    </div>
  );

  if (fieldsLoading) {
    return (
      <div className={"filter-conditions-display"}>
        <SkeletonLoader width={"50%"} />
      </div>
    );
  }
  const hasConditions = !!filterObj?.some((row) =>
    row?.conditions?.some((condition) => !!condition)
  );

  if (!!emptyState && !hasConditions) {
    return (
      <div className={`filter-conditions-display ${className}`}>
        {emptyState}
      </div>
    );
  }
  return (
    <div className={`filter-conditions-display ${className}`}>
      {isLegacyFilterWarningShown && (
        <div className={"legacy-filter-warning"}>
          <i
            className={
              "seem-icon seem-icon-warning legacy-filter-warning__icon"
            }
          />
          <span className={"legacy-filter-warning__text"}>
            {LEGACY_FILTER_WARNING_TEXT}
          </span>
        </div>
      )}
      {filterObj
        ?.slice(0, showFirstRowsCount || filterObj?.length)
        ?.map((filter, index) => (
          <div className={"scope-with-or"} key={index}>
            {index > 0 && <div className={`or-mark`}>OR</div>}
            {filter?.conditions
              ?.slice(
                0,
                showFirstConditionsInRowCount || filter?.conditions?.length
              )
              ?.map((condition: FilterItem, rowIndex: number) =>
                getFilterRow(condition, rowIndex)
              )}
          </div>
        ))}
    </div>
  );
};

export default FilterDisplayByFilterToExecute;
